import { selectFrequencies, selectHeading, selectSelectedService } from '../state/selectors';
import { formatDateLabel, formatTimeRange } from '../domain/dates';
import { useFormState, useMoney } from './context';
import { timeFormatOf } from './format';

export function SummaryBody({ compact = false }: { compact?: boolean }) {
  const money = useMoney();
  const state = useFormState();
  const settings = state.bootstrap?.settings ?? null;
  const service = selectSelectedService(state);
  const selection = state.values.services[0];
  const frequency = selectFrequencies(state).find((f) => f.id === state.values.frequencyId) ?? null;
  const est = state.estimate.data;
  const label = (code: string, fallback: string) => selectHeading(state, code) ?? fallback;
  const sysLabel = (code: 'service_date_summary' | 'frequency_summary', fallback: string) => state.bootstrap?.form.system_fields?.[code]?.label?.trim() || fallback;
  const extras = (service?.extras ?? []).filter((e) => typeof e.id === 'number' && (selection?.extras[e.id] ?? 0) > 0);
  const params = (service?.pricing_parameters ?? []).filter((p) => typeof p.id === 'number' && (selection?.pricingParameters[p.id] ?? 0) > 0);
  const slot = state.values.slot;
  const recurring = frequency && (frequency.interval ?? 'o') !== 'o';

  return (
    <>
      {service ? (
        <div className="cbf-sum-row">
          <span>{service.name}</span>
          <span className="cbf-mono">{typeof service.price === 'number' && service.price > 0 ? money(service.price) : ''}</span>
        </div>
      ) : (
        <div className="cbf-sum-row cbf-sum-muted">
          <span>{state.bootstrap?.form.system_fields?.service?.label?.replace(/\*+$/, '').trim() || 'Service'}</span>
          <span>—</span>
        </div>
      )}
      {params.map((p) => (
        <div key={p.id} className="cbf-sum-row cbf-sum-muted">
          <span className="cbf-sum-indent">
            {selection?.pricingParameters[p.id!]} {p.name}
          </span>
          <span className="cbf-mono">{money((p.price ?? 0) * (selection?.pricingParameters[p.id!] ?? 0))}</span>
        </div>
      ))}
      {extras.map((e) => (
        <div key={e.id} className="cbf-sum-row cbf-sum-muted">
          <span className="cbf-sum-indent">
            {e.name}
            {(selection?.extras[e.id!] ?? 1) > 1 ? ` × ${selection?.extras[e.id!]}` : ''}
          </span>
          <span className="cbf-mono">{money((e.price ?? 0) * (selection?.extras[e.id!] ?? 1))}</span>
        </div>
      ))}
      <div className={`cbf-sum-row${slot ? '' : ' cbf-sum-muted'}`}>
        <span>{slot ? `${formatDateLabel(slot.date)} · ${formatTimeRange(slot.hours, slot.minutes, slot.arrivalWindow, timeFormatOf(settings))}` : sysLabel('service_date_summary', 'Choose service date...')}</span>
      </div>
      <div className={`cbf-sum-row${frequency ? '' : ' cbf-sum-muted'}`}>
        <span>{frequency?.name ?? sysLabel('frequency_summary', 'Select frequency...')}</span>
      </div>
      {!compact ? <div className="cbf-sum-divider" /> : null}
      {est ? (
        <>
          <div className="cbf-sum-row cbf-sum-muted">
            <span>{label('booking_subtotal', 'Sub-total')}</span>
            <span className="cbf-mono">{money((est.services ?? 0) + (est.extras ?? 0))}</span>
          </div>
          {(est.discount_amount ?? 0) > 0 ? (
            <div className="cbf-sum-row cbf-sum-muted">
              <span>{label('booking_discount', 'Discount')}</span>
              <span className="cbf-mono">−{money(est.discount_amount ?? 0)}</span>
            </div>
          ) : null}
          {(est.giftcard_amount ?? 0) > 0 ? (
            <div className="cbf-sum-row cbf-sum-muted">
              <span>Gift card</span>
              <span className="cbf-mono">−{money(est.giftcard_amount ?? 0)}</span>
            </div>
          ) : null}
          <div className="cbf-sum-row" style={{ alignItems: 'baseline', paddingTop: 6 }}>
            <span className="cbf-sum-total-label">{label('booking_total', 'Total')}</span>
            <span className="cbf-sum-total cbf-mono">{money(est.total)}</span>
          </div>
          {est.discount_message ? <p className="cbf-sum-note">{est.discount_message}</p> : null}
          {recurring && typeof est.next_total === 'number' && est.next_total !== est.total ? (
            <p className="cbf-sum-note">Next visit {money(est.next_total)}.</p>
          ) : null}
        </>
      ) : (
        <div className="cbf-sum-row" style={{ alignItems: 'baseline' }}>
          <span className="cbf-sum-total-label">{label('booking_total', 'Total')}</span>
          <span className="cbf-sum-total cbf-mono">{state.estimate.status === 'loading' ? '…' : '—'}</span>
        </div>
      )}
      {!est && !compact ? <p className="cbf-sum-note">Pick a package and time to see your price.</p> : null}
    </>
  );
}

export function Summary() {
  const state = useFormState();
  const heading = selectHeading(state, 'booking_summary') ?? 'Booking summary';
  return (
    <aside className="cbf-summary" aria-label={heading}>
      <h2>{heading}</h2>
      <SummaryBody />
    </aside>
  );
}
